/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * CDU-consent-aware localStorage wrapper. Routes non-essential keys to in-memory
 * storage until consent is accepted, while essential keys always hit real localStorage.
 */

import { isEssentialStorageKey } from './essentialStorageKeys';
import { originalLocalStorage, getInMemoryStorage, hasConsent } from './localStorage-in-memory';

export {
  migrateInMemoryToLocalStorage,
  clearInMemoryStorage,
  getInMemoryStorageContents,
} from './localStorage-in-memory';

/**
 * Decides whether a key/consent combination routes to real localStorage.
 * Essential keys always use real storage; others require accepted consent.
 *
 * @param key Storage key being accessed.
 * @returns True when the operation must target real localStorage.
 */
function useRealStorage(key: string): boolean {
  return isEssentialStorageKey(key) || hasConsent();
}

/**
 * Collects the essential keys currently present in real localStorage, in order.
 *
 * @returns The essential keys, preserving their real-localStorage index order.
 */
function collectEssentialKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < originalLocalStorage.length; i++) {
    const k = originalLocalStorage.key(i);
    if (k && isEssentialStorageKey(k)) keys.push(k);
  }
  return keys;
}

/**
 * Reads an item, honoring the essential-key/consent routing rules.
 *
 * @param key Storage key to read.
 * @returns The stored value, or null when absent.
 */
function wrappedGetItem(key: string): string | null {
  const backend = useRealStorage(key) ? originalLocalStorage : getInMemoryStorage();
  return backend.getItem(key);
}

/**
 * Writes an item, honoring the essential-key/consent routing rules.
 *
 * @param key Storage key to write.
 * @param value Value to store.
 */
function wrappedSetItem(key: string, value: string): void {
  const backend = useRealStorage(key) ? originalLocalStorage : getInMemoryStorage();
  backend.setItem(key, value);
}

/**
 * Removes an item, honoring the essential-key/consent routing rules.
 *
 * @param key Storage key to remove.
 */
function wrappedRemoveItem(key: string): void {
  const backend = useRealStorage(key) ? originalLocalStorage : getInMemoryStorage();
  backend.removeItem(key);
}

/**
 * Clears storage: real localStorage when consent is granted, else in-memory.
 */
function wrappedClear(): void {
  const backend = hasConsent() ? originalLocalStorage : getInMemoryStorage();
  backend.clear();
}

/**
 * Computes the wrapper length: real length with consent, otherwise the count of
 * essential keys plus the in-memory entry count.
 *
 * @returns The number of accessible entries.
 */
function wrappedLength(): number {
  if (hasConsent()) {
    return originalLocalStorage.length;
  }
  return collectEssentialKeys().length + getInMemoryStorage().length;
}

/**
 * Returns the key at a given index: real key with consent, otherwise essential
 * keys first, then in-memory keys.
 *
 * @param index Zero-based key index.
 * @returns The key at the index, or null when out of range.
 */
function wrappedKey(index: number): string | null {
  if (hasConsent()) {
    return originalLocalStorage.key(index);
  }
  const essentialKeys = collectEssentialKeys();
  if (index < essentialKeys.length) {
    return essentialKeys[index];
  }
  return getInMemoryStorage().key(index - essentialKeys.length);
}

/**
 * Creates a localStorage wrapper that enforces CDU consent.
 *
 * Rules:
 * - Essential keys (OIDC tokens, JWKS, returnPath): ALWAYS use real localStorage
 *   regardless of consent status (strictly necessary under LGPD/GDPR).
 * - Non-essential keys when consent is ACCEPTED: Use real localStorage
 * - Non-essential keys when consent is PENDING/REJECTED: Use in-memory storage
 *
 * @returns {Storage} Wrapped localStorage object
 */
export function createLocalStorageWrapper(): Storage {
  return {
    getItem: wrappedGetItem,
    setItem: wrappedSetItem,
    removeItem: wrappedRemoveItem,
    clear: wrappedClear,
    get length(): number {
      return wrappedLength();
    },
    key: wrappedKey,
  };
}

/**
 * Returns the real (unwrapped) localStorage, bypassing the CDU consent wrapper.
 * Use for functional keys that must persist across page reloads regardless of
 * consent status (e.g. returnPath, referral codes).
 */
export function getRealLocalStorage(): Storage {
  return (window as any).__originalLocalStorage || window.localStorage;
}

/**
 * Initializes the localStorage wrapper by replacing window.localStorage.
 * This should be called as early as possible in the application lifecycle,
 * before any other code that uses localStorage.
 *
 * @returns {void}
 */
export function initializeLocalStorageWrapper(): void {
  if ((window as any).__originalLocalStorage) {
    return;
  }

  (window as any).__originalLocalStorage = originalLocalStorage;

  Object.defineProperty(window, 'localStorage', {
    writable: true,
    configurable: true,
    value: createLocalStorageWrapper(),
  });

  console.log('[LocalStorageWrapper] Initialized - using in-memory storage until CDU consent is accepted');
}
